import React, {useCallback, useEffect, useState} from 'react';
import {axios} from '../../libs';
import logger from '../../utils/logger';

export function ClaudeConnectorModal({isOpen, onClose, chatbotId, botName}) {
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState({connected: false});
  const [connectorUrl, setConnectorUrl] = useState('');
  const [readOnly, setReadOnly] = useState(false);
  const [allowChatData, setAllowChatData] = useState(false);
  const [isReference, setIsReference] = useState(false);
  const [referenceNote, setReferenceNote] = useState('');
  const [message, setMessage] = useState('');

  const loadStatus = useCallback(async () => {
    if (!chatbotId) return;
    try {
      setLoading(true);
      const data = await axios.get(`/claude-connector/${chatbotId}`);
      setStatus(data);
      setReadOnly(Boolean(data.readOnly));
      setAllowChatData(Boolean(data.allowChatData));
      setIsReference(Boolean(data.isReference));
      setReferenceNote(data.referenceNote || '');
    } catch (e) {
      logger.error(e);
      setMessage('Could not load connector status.');
    } finally {
      setLoading(false);
    }
  }, [chatbotId]);

  useEffect(() => {
    if (isOpen) {
      setConnectorUrl('');
      setMessage('');
      loadStatus();
    }
  }, [isOpen, loadStatus]);

  const generate = async () => {
    try {
      setLoading(true);
      setMessage('');
      const data = await axios.post(`/claude-connector/${chatbotId}`, {});
      setConnectorUrl(data.connectorUrl || '');
      setStatus((s) => ({...s, connected: true, tokenPrefix: data.tokenPrefix, enabled: true}));
      setMessage('Copy the URL now. It is shown only once.');
    } catch (e) {
      logger.error(e);
      setMessage('Failed to generate connector URL.');
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    try {
      setLoading(true);
      const payload = {isReference, referenceNote};
      if (status.connected) {
        payload.readOnly = readOnly;
        if (status.chatDataAvailable) {
          payload.allowChatData = allowChatData;
        }
      }
      const data = await axios.patch(`/claude-connector/${chatbotId}`, payload);
      setStatus((s) => ({
        ...s,
        readOnly: data.readOnly ?? s.readOnly,
        allowChatData: data.allowChatData ?? s.allowChatData,
        enabled: data.enabled ?? s.enabled,
        isReference: data.isReference ?? isReference,
        referenceNote: data.referenceNote ?? referenceNote,
      }));
      setMessage('Settings saved.');
    } catch (e) {
      logger.error(e);
      setMessage('Failed to save settings.');
    } finally {
      setLoading(false);
    }
  };

  const revoke = async () => {
    if (!window.confirm('Revoke this connector? Claude will lose access until you generate a new URL.')) {
      return;
    }
    try {
      setLoading(true);
      await axios.delete(`/claude-connector/${chatbotId}`);
      setConnectorUrl('');
      setStatus({connected: false});
      setMessage('Connector revoked.');
    } catch (e) {
      logger.error(e);
      setMessage('Failed to revoke.');
    } finally {
      setLoading(false);
    }
  };

  const copyUrl = async () => {
    if (!connectorUrl) return;
    try {
      await navigator.clipboard.writeText(connectorUrl);
      setMessage('URL copied to clipboard.');
    } catch {
      setMessage('Copy failed — select and copy manually.');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="claude-connector-overlay" onClick={onClose} role="presentation">
      <div
        className="claude-connector-panel"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-labelledby="claude-connector-title"
      >
        <h6 id="claude-connector-title">Claude connector — {botName || chatbotId}</h6>
        <p className="text-muted small">
          One secret URL binds Claude to this bot only. Treat it like a password.
        </p>

        {status.connected && (
          <ul className="small" style={{paddingLeft: 18}}>
            <li>Status: {status.enabled === false ? 'disabled' : 'active'}</li>
            <li>Token prefix: {status.tokenPrefix || '—'}</li>
            <li>Last used: {status.lastUsedAt ? new Date(status.lastUsedAt).toLocaleString() : 'never'}</li>
          </ul>
        )}

        <label className="d-flex align-items-center gap-2 mb-2 small">
          <input type="checkbox" checked={readOnly} onChange={(e) => setReadOnly(e.target.checked)} disabled={!status.connected} />
          Read-only (export and validate only)
        </label>

        {status.connected && status.chatDataAvailable && (
          <label className="d-flex align-items-center gap-2 mb-2 small">
            <input
              type="checkbox"
              checked={allowChatData}
              onChange={(e) => setAllowChatData(e.target.checked)}
            />
            Chat data access (apptest only — flow errors and conversation variables)
          </label>
        )}

        <label className="d-flex align-items-center gap-2 mb-2 small">
          <input
            type="checkbox"
            checked={isReference}
            onChange={(e) => setIsReference(e.target.checked)}
          />
          Use as reference app for all connectors (read-only export)
        </label>
        {isReference && (
          <div className="mb-2">
            <input
              type="text"
              className="form-control form-control-sm"
              placeholder="What this app demonstrates (e.g. testament intake + AI doc mode)"
              value={referenceNote}
              onChange={(e) => setReferenceNote(e.target.value)}
            />
          </div>
        )}

        {connectorUrl && (
          <div className="mb-2">
            <textarea className="form-control form-control-sm" rows={3} readOnly value={connectorUrl} />
            <button type="button" className="btn btn-sm btn-outline-primary mt-1" onClick={copyUrl}>
              Copy URL
            </button>
          </div>
        )}

        <div className="small mb-2">
          <strong>Add in Claude:</strong> Settings → Connectors → Add custom connector → paste URL (no OAuth).
          In chat, enable the connector from the tools menu.
        </div>

        {message && <p className="small text-info">{message}</p>}

        <div className="d-flex flex-wrap gap-2 mt-2">
          <button type="button" className="btn btn-primary btn-sm" disabled={loading} onClick={generate}>
            {status.connected ? 'Rotate URL' : 'Generate URL'}
          </button>
          <button type="button" className="btn btn-secondary btn-sm" disabled={loading} onClick={saveSettings}>
            Save settings
          </button>
          <button type="button" className="btn btn-outline-danger btn-sm" disabled={loading || !status.connected} onClick={revoke}>
            Revoke
          </button>
          <button type="button" className="btn btn-link btn-sm" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
